/**
 * @file Upload session outcome summarization for completion handlers and toasts.
 */

import type { UploadFileStatus, UploadResourceSession } from './types';

const SETTLED_STATUSES: UploadFileStatus[] = ['complete', 'cancelled', 'error'];

/** Aggregated counts and flags for a settled or in-progress session. */
export type UploadSessionOutcome = {
  totalCount: number;
  completeCount: number;
  errorCount: number;
  cancelledCount: number;
  isSettled: boolean;
  isFullSuccess: boolean;
  hasPartialSuccess: boolean;
  hasAnySuccess: boolean;
};

/**
 * Whether every file in the session has reached a terminal status.
 * @param session - Upload session to evaluate.
 */
export const isSessionSettled = (session: UploadResourceSession): boolean =>
  session.files.length > 0 &&
  session.files.every((file) => SETTLED_STATUSES.includes(file.status));

/** Session ids that already finished in a prior page load (completion toast shown). */
export const getPreviouslyNotifiedSessionIds = (
  sessions: UploadResourceSession[]
): string[] =>
  sessions
    .filter((session) => session.completedAt != null)
    .map((session) => session.id);

/** Session completion timestamps already handled (e.g. after reload). */
export const getPreviouslyNotifiedSessionCompletions = (
  sessions: UploadResourceSession[]
): Map<string, string> => {
  const completions = new Map<string, string>();

  for (const session of sessions) {
    if (session.completedAt != null) {
      completions.set(session.id, session.completedAt);
    }
  }

  return completions;
};

/**
 * Whether {@link UploadKindAdapter.onSessionComplete} should run for this settlement.
 * Matches on session id + completedAt so re-uploads on the same resource can toast again.
 */
export const shouldRunSessionCompletionHandler = (
  session: UploadResourceSession,
  handledCompletions: ReadonlyMap<string, string>,
  inFlightSessionIds: ReadonlySet<string>
): boolean =>
  session.completedAt != null &&
  handledCompletions.get(session.id) !== session.completedAt &&
  !inFlightSessionIds.has(session.id);

/**
 * Computes success/error/cancelled counts and settlement flags for a session.
 * @param session - Upload session to summarize.
 */
export const summarizeUploadSession = (
  session: UploadResourceSession
): UploadSessionOutcome => {
  const completeCount = session.files.filter(
    (file) => file.status === 'complete'
  ).length;
  const errorCount = session.files.filter(
    (file) => file.status === 'error'
  ).length;
  const cancelledCount = session.files.filter(
    (file) => file.status === 'cancelled'
  ).length;
  const totalCount = session.files.length;
  const isSettled = isSessionSettled(session);
  const isFullSuccess = totalCount > 0 && completeCount === totalCount;
  const hasAnySuccess = completeCount > 0;
  const hasPartialSuccess = completeCount > 0 && errorCount > 0;

  return {
    totalCount,
    completeCount,
    errorCount,
    cancelledCount,
    isSettled,
    isFullSuccess,
    hasPartialSuccess,
    hasAnySuccess,
  };
};
